import { Alert, Badge, Button, Group, Stack, Text } from '@mantine/core'
import { H1, SortableTable } from '@packages/ui'
import { useSupabase } from '@packages/supabase'
import {
  IconAlertTriangle,
  IconCircleChevronLeft,
  IconRefresh,
  IconRestore
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

interface PendingRow {
  tenant_id: string
  tenant_name: string | null
  requested_at: string
  requested_by_email: string | null
  grace_until: string
  days_remaining: number
  reason: string | null
  log_id: string | null
  in_progress: boolean
}

export const PendingDeletions = () => {
  const { supabase } = useSupabase()
  const navigate = useNavigate()
  const [rows, setRows] = useState<PendingRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [restoring, setRestoring] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error } = await supabase.rpc(
      'admin_list_pending_tenant_deletions'
    )
    setLoading(false)
    if (error) {
      setError(error.message)
      return
    }
    setRows((data as PendingRow[]) ?? [])
  }, [supabase])

  useEffect(() => {
    fetch()
  }, [fetch])

  const restore = async (tenantId: string) => {
    setRestoring(tenantId)
    const { error: rpcErr } = await supabase.rpc(
      'admin_cancel_tenant_deletion',
      { p_tenant_id: tenantId, p_reason: 'Restored from pending list' }
    )
    setRestoring(null)
    if (rpcErr) {
      setError(rpcErr.message)
      return
    }
    await fetch()
  }

  return (
    <Stack c="white" gap="md">
      <Group justify="space-between">
        <H1 className="text-white">
          <Button
            variant="transparent"
            c="white"
            leftSection={<IconCircleChevronLeft />}
            onClick={() => navigate('/tenants')}
            p={0}
          >
            Back
          </Button>{' '}
          Pending Deletions
        </H1>
        <Button
          variant="transparent"
          c="white"
          leftSection={<IconRefresh size={16} />}
          loading={loading}
          onClick={fetch}
        >
          Refresh
        </Button>
      </Group>

      <Alert color="blue" icon={<IconAlertTriangle />}>
        These tenants are scheduled for permanent deletion. Restoring cancels
        the deletion and restores user access. Once a tenant enters
        <strong> in&nbsp;progress</strong>, it can no longer be restored.
      </Alert>

      {error && <Text c="red">{error}</Text>}

      <SortableTable
        data={rows}
        pageSize={25}
        columns={[
          { key: 'tenant_name', label: 'Tenant' },
          {
            key: 'requested_at',
            label: 'Requested',
            render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm')
          },
          { key: 'requested_by_email', label: 'Requested by' },
          {
            key: 'grace_until',
            label: 'Purge at',
            render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm')
          },
          {
            key: 'days_remaining',
            label: 'Days left',
            render: (v: number, row: PendingRow) =>
              row.in_progress ? (
                <Badge color="red">in progress</Badge>
              ) : (
                <Badge color={v > 5 ? 'green' : v > 0 ? 'yellow' : 'red'}>
                  {v}
                </Badge>
              )
          },
          { key: 'reason', label: 'Reason' },
          {
            key: 'tenant_id',
            label: 'Action',
            render: (_v, row: PendingRow) =>
              row.in_progress ? (
                <Text size="xs" c="dimmed">
                  locked
                </Text>
              ) : (
                <Group gap={6}>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() =>
                      navigate(`/tenants/${row.tenant_id}`, {
                        state: { tenant: { id: row.tenant_id } }
                      })
                    }
                  >
                    Open
                  </Button>
                  <Button
                    size="xs"
                    color="green"
                    leftSection={<IconRestore size={14} />}
                    loading={restoring === row.tenant_id}
                    onClick={() => restore(row.tenant_id)}
                  >
                    Restore
                  </Button>
                </Group>
              )
          }
        ]}
      />
    </Stack>
  )
}

export default PendingDeletions
